import { Box, Flex, Grid, GridItem, Text, VStack } from '@chakra-ui/react';
import {
  AgentComplianceReviewByShareLinkTokenQuery,
  IngestionPlatform,
} from '@client/graphql/__generated__/types';
import { FC } from 'react';
import {
  ChecklistNavFooter,
  DocumentIssuesPanel,
  DocumentTabs,
  DocumentTitleBar,
  DocumentViewer,
  FolderSuggestionsCard,
  NeedsReviewChecklist,
  OtherDocumentsList,
  PropertySummaryCard,
} from './components';
import { useAgentComplianceReview } from './hooks/useAgentComplianceReview';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewDesktopProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document tab (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

/**
 * Desktop layout for the agent compliance review page.
 *
 * - **Left rail**: property summary, folder suggestions, the "Needs review"
 *   checklist (SkySlope checklist items shown directly, no grouping), and the
 *   collapsible "Other documents" list.
 * - **Center**: the active checklist item's title + actions, a tab strip for
 *   its documents, the {@link DocumentViewer} for the selected tab beside the
 *   {@link DocumentIssuesPanel}, and a pinned footer to cycle Needs-Review items.
 *
 * All behavior comes from {@link useAgentComplianceReview}, shared with the
 * mobile layout. Auto-selects the first Needs-Review item on load.
 */
export const AgentComplianceReviewDesktop: FC<
  AgentComplianceReviewDesktopProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const {
    document,
    loading,
    error,
    onPdfLoadError,
    activeFieldIds,
    validation,
    needsReviewItems,
    otherItems,
    activeItem,
    activeItemDocs,
    tabSuggestionCounts,
    itemNeedsReview,
    itemResubmitted,
    isResubmitReady,
    itemDocsError,
    retryItemDocs,
    platformRequirementIssues,
    platformUrl,
    onOpenPlatform,
    isIgnoring,
    isIgnoringPlatformIssue,
    isSubmitting,
    defaultResubmitMessage,
    showChecklistNav,
    hasPrevChecklistItem,
    hasNextChecklistItem,
    onSelectNextChecklistItem,
    onSelectPrevChecklistItem,
    onSelectFields,
    onSelectDocument,
    onIgnoreDocumentSuggestion,
    onUndoIgnoreDocumentSuggestion,
    onIgnoreFolderSuggestion,
    onUndoIgnoreFolderSuggestion,
    onIgnorePlatformRequirementIssue,
    onUndoIgnorePlatformRequirementIssue,
    onResubmit,
  } = useAgentComplianceReview({
    overview,
    selectedDocumentId,
    token,
    ingestionFolderUuid,
    autoSelectFirstNeedsReview: true,
  });

  // SkySlope keeps its flat checklist-activity rail; other platforms (Dotloop)
  // supplement the same needs-review/other split with folder grouping.
  const isSkySlope = overview.platform === IngestionPlatform.SKYSLOPE;

  return (
    <Grid
      bg="compliance.background.primary"
      gap={0}
      h="100dvh"
      maxH="100dvh"
      overflow="hidden"
      templateColumns="360px 1fr"
      templateRows="minmax(0, 1fr)"
      w="full"
    >
      {/* Left rail */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderRightColor="compliance.sidebar.border"
        borderRightWidth={1}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <FolderSuggestionsCard
            isIgnoring={isIgnoring}
            suggestions={overview.folderSuggestions}
            onIgnore={onIgnoreFolderSuggestion}
            onUndoIgnore={onUndoIgnoreFolderSuggestion}
          />

          <NeedsReviewChecklist
            activeItemId={activeItem?.id}
            items={needsReviewItems}
            showFolderName={!isSkySlope}
            onSelectDocument={onSelectDocument}
          />

          <OtherDocumentsList
            activeItemId={activeItem?.id}
            groupByFolder={!isSkySlope}
            items={otherItems}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </GridItem>

      {/* Center: title + tabs + PDF/issues + nav footer */}
      <GridItem h="full" minH={0} minWidth={0} overflow="hidden">
        {error ? (
          <Flex
            align="center"
            bg="compliance.pdf.bg"
            h="full"
            justify="center"
            p={6}
            w="full"
          >
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document. Please try again.
            </Text>
          </Flex>
        ) : activeItem == null ? (
          <Flex
            align="center"
            bg="compliance.pdf.bg"
            h="full"
            justify="center"
            p={6}
            w="full"
          >
            <Text color="compliance.text.muted" fontSize="sm">
              Select a checklist item to review it here.
            </Text>
          </Flex>
        ) : (
          <Flex direction="column" h="full" minH={0} w="full">
            <DocumentTitleBar
              defaultResubmitMessage={defaultResubmitMessage}
              isResubmitReady={isResubmitReady}
              isResubmitted={itemResubmitted}
              isSubmitting={isSubmitting}
              needsReview={itemNeedsReview}
              platform={overview.platform}
              platformUrl={platformUrl}
              resubmitLoadError={itemDocsError}
              title={activeItem.title}
              onOpenPlatform={onOpenPlatform}
              onResubmit={onResubmit}
              onRetryResubmitLoad={retryItemDocs}
            />

            {/* Tabs switch between the documents of a SkySlope checklist item.
                Non-SkySlope items are a single document, so there is nothing to
                switch between — hide the strip. */}
            {isSkySlope && (
              <DocumentTabs
                docs={activeItemDocs}
                selectedDocumentId={selectedDocumentId}
                suggestionCounts={tabSuggestionCounts}
                onSelectDocument={onSelectDocument}
              />
            )}

            <Flex flex={1} minH={0} w="full">
              <Box flex={1} minW={0}>
                <DocumentViewer
                  activeFieldIds={activeFieldIds}
                  documentName={document?.documentName}
                  loading={loading}
                  pdfUrl={document?.pdfUrl}
                  submittedAt={
                    document?.submittedAt != null
                      ? String(document.submittedAt)
                      : undefined
                  }
                  successFieldIds={validation.successFieldIds}
                  validationFieldsMap={validation.validationFieldsMap}
                  warningFieldIds={validation.errorFieldIds}
                  warningFieldMessages={validation.fieldMessages}
                  onPdfLoadError={onPdfLoadError}
                />
              </Box>

              <Box
                bg="compliance.sidebar.bg"
                borderLeftColor="compliance.sidebar.border"
                borderLeftWidth={1}
                h="full"
                minH={0}
                overflowY="auto"
                p={4}
                w="340px"
              >
                <DocumentIssuesPanel
                  contractDocumentCount={validation.contractDocumentCount}
                  hasDetail={document != null}
                  isIgnoring={isIgnoring}
                  isIgnoringPlatformRequirementIssue={isIgnoringPlatformIssue}
                  isSkySlope={isSkySlope}
                  loading={loading}
                  platformRequirementIssues={platformRequirementIssues}
                  successfulChecks={validation.successfulChecks}
                  suggestions={validation.suggestions}
                  onIgnorePlatformRequirementIssue={
                    onIgnorePlatformRequirementIssue
                  }
                  onIgnoreSuggestion={onIgnoreDocumentSuggestion}
                  onSelectFields={onSelectFields}
                  onUndoIgnorePlatformRequirementIssue={
                    onUndoIgnorePlatformRequirementIssue
                  }
                  onUndoIgnoreSuggestion={onUndoIgnoreDocumentSuggestion}
                />
              </Box>
            </Flex>

            {showChecklistNav && (
              <ChecklistNavFooter
                hasNext={hasNextChecklistItem}
                hasPrev={hasPrevChecklistItem}
                itemNoun={isSkySlope ? 'checklist item' : 'item'}
                onNext={onSelectNextChecklistItem}
                onPrev={onSelectPrevChecklistItem}
              />
            )}
          </Flex>
        )}
      </GridItem>
    </Grid>
  );
};
